import { ToggleTip } from '@figma/fpl-components';
import { useTranslation } from 'react-i18next';
import { Box } from '@/UI/Box/Box';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { Tooltip, type TooltipProps } from '@/UI/Tooltip/Tooltip';
import { InfoIcon } from '../Icons';
import type { ComponentProps, ReactNode } from 'react';

type AppInfoIconProps = {
  tooltipProps: Omit<TooltipProps, 'children'>;
  size?: number;
};

type FplToggleTipOptions = Parameters<typeof ToggleTip.useUncontrolledToggleTip>[0];
type FplAppInfoIconProps = Pick<FplToggleTipOptions, 'placement'> &
  Pick<ComponentProps<typeof ToggleTip.IconTrigger>, 'aria-label'> & {
    children: ReactNode;
    icon: ReactNode;
  };

function mapLegacyPlacementToFpl(placement: TooltipProps['placement']): FplToggleTipOptions['placement'] {
  if (placement === 'auto') return 'bottom';
  if (placement === 'auto-start') return 'bottom-start';
  if (placement === 'auto-end') return 'bottom-end';
  return placement ?? 'bottom';
}

function FplAppInfoIcon({ 'aria-label': ariaLabel, children, icon, placement = 'bottom' }: FplAppInfoIconProps) {
  const manager = ToggleTip.useUncontrolledToggleTip({ placement });
  const isTextContent = typeof children === 'string' || typeof children === 'number';

  return (
    <>
      <ToggleTip.IconTrigger aria-label={ariaLabel} {...manager.getTriggerProps()}>
        {icon}
      </ToggleTip.IconTrigger>
      <ToggleTip.Container manager={manager}>
        {isTextContent ? <ToggleTip.Content>{children}</ToggleTip.Content> : children}
      </ToggleTip.Container>
    </>
  );
}

export function AppInfoIcon({ tooltipProps, size = 16 }: AppInfoIconProps) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled) {
    return (
      <FplAppInfoIcon
        aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_HELP)}
        icon={
          // `fplIcon` drops width/height props on the FPL glyph, so `size` has to be applied as CSS
          // on the svg itself or the icon falls back to the FPL intrinsic size (24). Colour is left
          // to the glyph's own `var(--fpl-icon-color, var(--color-icon))` fill.
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <InfoIcon />
          </Box>
        }
        placement={mapLegacyPlacementToFpl(tooltipProps.placement)}
      >
        {tooltipProps.title}
      </FplAppInfoIcon>
    );
  }

  // The wrapped InfoIcon no longer forwards a ref, so give MUI's Tooltip a ref-able span to anchor.
  return (
    <Tooltip {...tooltipProps}>
      <span style={{ display: 'inline-flex' }}>
        <InfoIcon width={size} height={size} />
      </span>
    </Tooltip>
  );
}
